import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditableSection from '../EditableSection';
import InputField from '../ui/InputField';
import Toggle from '../ui/Toggle';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import {
    useGetInvoiceCircleQuery,
    useSaveInvoiceCircleMutation,
} from '../../api/invoice/invoiceApiSlice';
import { useInvoiceMasterSlug } from '../../hooks/useIsInvoiceIssuingTenant';
import { errorMessageFrom } from '../../utils/errorMessage';

interface Props {
    /** The tenant's ADR-0012 mode. A circle is only meaningful for `kolula`. */
    invoiceIssuer: string | null | undefined;
    /** No edit rights: the view only, no "Bearbeiten". */
    readOnly?: boolean;
}

/** The form's values — strings where the field is a text input. */
interface CircleDraft {
    prefix: string;
    formatTemplate: string;
    nextSeq: string;
    seqPadding: string;
    resetYearly: boolean;
}

/** What a tenant without a circle starts from. */
const NEW_CIRCLE: CircleDraft = {
    prefix: '',
    formatTemplate: '{PREFIX}-{YYYY}-{SEQ}',
    nextSeq: '1',
    seqPadding: '4',
    resetYearly: true,
};

const previewOf = ({ prefix, formatTemplate, nextSeq, seqPadding }: CircleDraft): string =>
    formatTemplate
        .replace(/\{PREFIX\}/g, prefix || '…')
        .replace(/\{YYYY\}/g, String(new Date().getFullYear()))
        .replace(/\{SEQ\}/g, (nextSeq || '1').padStart(Math.max(1, Number(seqPadding) || 1), '0'));

/**
 * The Zahlenreihe kolula allocates invoice numbers from (#1637, WP1).
 *
 * Seeded once by the tenant so their overall numbering stays systematic:
 * §14 (4) Nr. 4 UStG allows several Zahlenreihen, and the prefix is what keeps
 * kolula's range from colliding with the one their own system allocates from.
 *
 * Frozen as soon as the circle has issued anything — the unit offers no
 * "Bearbeiten" and says why. Changing the prefix or the counter afterwards
 * would either re-point the range at numbers already used or leave a hole in
 * it, and issued invoices are never renumbered.
 *
 * One editable unit (design-decisions › Bearbeiten und Speichern): the view
 * shows the stored circle, derived from the API on every render, and
 * "Bearbeiten" opens a draft of it.
 */
const InvoiceCircleSection = ({ invoiceIssuer, readOnly = false }: Readonly<Props>) => {
    const { t } = useTranslation();
    const isIssuingTenant = invoiceIssuer === 'kolula';

    // A master addresses the displayed tenant by slug; an owner passes none
    // and gets their own. Without this a master 404s on `account_no_tenant`.
    const masterSlug = useInvoiceMasterSlug();
    const { data, isLoading } = useGetInvoiceCircleQuery(masterSlug, { skip: !isIssuingTenant });
    const [save, { isLoading: saving }] = useSaveInvoiceCircleMutation();

    const circle = data?.circle ?? null;
    const frozen = (circle?.issuedCount ?? 0) > 0;

    const saved = useMemo<CircleDraft>(() => (circle ? {
        prefix: circle.prefix,
        formatTemplate: circle.formatTemplate,
        nextSeq: String(circle.nextSeq),
        seqPadding: String(circle.seqPadding),
        resetYearly: circle.resetYearly,
    } : NEW_CIRCLE), [circle]);
    const draft = useEditableDraft<CircleDraft>(saved);
    const [error, setError] = useState<string | null>(null);

    if (!isIssuingTenant) return null;

    const handleSave = async () => {
        setError(null);
        const { prefix, formatTemplate, nextSeq, seqPadding, resetYearly } = draft.value;
        try {
            await save({
                ...(masterSlug ? { slug: masterSlug } : {}),
                prefix,
                formatTemplate,
                nextSeq: Number(nextSeq),
                seqPadding: Number(seqPadding),
                resetYearly,
            }).unwrap();
            draft.finish();
        } catch (err: unknown) {
            // The server's own message: a bad prefix, a template without
            // {SEQ} and a frozen circle are three different problems.
            setError(errorMessageFrom(err, t('account.invoice_issue_failed'), t));
        }
    };

    /** The circle — locked in the view, editable in the draft. */
    const renderCircle = (locked: boolean) => {
        const value = locked ? saved : draft.value;
        const disabled = locked || saving;
        const set = (patch: Partial<CircleDraft>) => draft.setDraft({ ...draft.value, ...patch });
        return (
            <div>
                {locked && frozen ? (
                    <p className={'text-sm text-foreground/80 mb-4'}>
                        {t('account.invoice_circle_frozen', { count: circle?.issuedCount ?? 0 })}
                    </p>
                ) : null}

                {locked && !isLoading && !circle ? (
                    <p className={'text-sm text-foreground/60 mb-4'}>{t('account.invoice_circle_none')}</p>
                ) : null}

                <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                    <InputField
                        labelAbove
                        label={t('account.invoice_circle_prefix')}
                        help={t('account.invoice_circle_prefix_help')}
                        value={value.prefix}
                        onChange={(prefix) => set({ prefix })}
                        disabled={disabled}
                    />
                    <InputField
                        labelAbove
                        label={t('account.invoice_circle_format')}
                        help={t('account.invoice_circle_format_help')}
                        value={value.formatTemplate}
                        onChange={(formatTemplate) => set({ formatTemplate })}
                        disabled={disabled}
                    />
                    <InputField
                        labelAbove
                        label={t('account.invoice_circle_next_seq')}
                        help={t('account.invoice_circle_next_seq_help')}
                        value={value.nextSeq}
                        onChange={(nextSeq) => set({ nextSeq })}
                        disabled={disabled}
                    />
                    <InputField
                        labelAbove
                        label={t('account.invoice_circle_padding')}
                        value={value.seqPadding}
                        onChange={(seqPadding) => set({ seqPadding })}
                        disabled={disabled}
                    />
                </div>

                <div className={'mt-4'}>
                    <Toggle
                        checked={value.resetYearly}
                        onChange={(resetYearly) => set({ resetYearly })}
                        label={t('account.invoice_circle_reset_yearly')}
                        disabled={disabled}
                    />
                </div>

                <p className={'mt-4 text-sm'}>
                    <span className={'text-foreground/60'}>{t('account.invoice_circle_preview')}: </span>
                    <span className={'font-medium text-foreground'}>{previewOf(value)}</span>
                </p>
            </div>
        );
    };

    return (
        <EditableSection
            title={t('account.invoice_circle_section')}
            description={t('account.invoice_circle_desc')}
            // Frozen: issued numbers are never renumbered. Loading: nothing
            // to open a draft of yet.
            readOnly={readOnly || frozen || isLoading}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            canSave={!!draft.value.prefix.trim()}
            error={error}
            onEdit={draft.startEdit}
            onCancel={() => { setError(null); draft.cancel(); }}
            onSave={() => { void handleSave(); }}
            view={renderCircle(true)}
        >
            {renderCircle(false)}
        </EditableSection>
    );
};

export default InvoiceCircleSection;
